import { AddCircleRounded, Remove } from '@mui/icons-material';
import {
  Box,
  Button,
  IconButton,
  Stack,
  TextField,
  Typography,
} from '@mui/material';
import {
  GridCellModes,
  GridCellModesModel,
  GridCellParams,
  GridColDef,
} from '@mui/x-data-grid-premium';
import { FieldConfig, FieldInputProps, FormikErrors } from 'formik';
import { MouseEvent, useCallback, useMemo, useState } from 'react';
import {
  ProductCreateWithVariants,
  ProductVariantBase,
  ProductWithVariants,
  SuggestedPrice,
  VariesBy,
} from 'src/app/api/openapi';
import CustomDataGrid from 'src/app/components/CustomDataGrid';

interface CreateVariantTableProps {
  values: ProductCreateWithVariants;
  setFieldValue: (
    field: string,
    value: any,
    shouldValidate?: boolean
  ) => Promise<void | FormikErrors<ProductWithVariants>>;
  getFieldProps: <Value = any>(
    props: string | FieldConfig<Value>
  ) => FieldInputProps<Value>;
}

const CreateVariantsTable = ({
  values,
  setFieldValue,
  getFieldProps,
}: CreateVariantTableProps) => {
  const [newProperty, setNewProperty] = useState<string | null>(null);

  const variesByValueGetter =
    (field: string) => (value: string, row: ProductVariantBase) =>
      row.varies_by &&
      row.varies_by.find((variesBy) => variesBy.name === field)?.value;

  const variesByValueSetter =
    (field: string) => (value: string, row: ProductVariantBase) => ({
      ...row,
      varies_by:
        value && row.varies_by
          ? row.varies_by.map((property: VariesBy) =>
              property.name === field ? { name: field, value: value } : property
            )
          : [],
    });

  const columns: GridColDef<ProductVariantBase>[] = useMemo(
    () => [
      {
        headerName: 'Price',
        field: 'suggested_prices',
        valueGetter: (value: SuggestedPrice[]) =>
          (value && value.length && value[0].amount) || '',
        valueSetter: (value, row) => {
          return {
            ...row,
            suggested_prices: [{ currency_code: 'USD', amount: value }],
          };
        },
        editable: true,
        flex: 1,
      },
      {
        field: 'gtin',
        headerName: 'GTIN',
        editable: true,
        flex: 1,
      },
      {
        field: 'sku',
        headerName: 'SKU',
        editable: true,
        flex: 1,
      },
      ...(values.varies_by || []).map((property) => ({
        field: property,
        renderHeader: ({ field }: any) => (
          <Typography fontWeight={700}>
            {property}
            <IconButton
              onClick={() => {
                setFieldValue(
                  'varies_by',
                  values.varies_by?.filter((variesBy) => variesBy !== field)
                );
                values.variants.forEach((variant, i) =>
                  setFieldValue(
                    `variants[${i}].varies_by`,
                    variant.varies_by?.filter(
                      (variesBy) => variesBy.name !== field
                    )
                  )
                );
              }}
            >
              <Remove />
            </IconButton>
          </Typography>
        ),
        valueGetter: variesByValueGetter(property),
        valueSetter: variesByValueSetter(property),
        editable: true,
        flex: 1,
      })),
    ],
    [values]
  );

  // single click
  const [cellModesModel, setCellModesModel] = useState<GridCellModesModel>({});

  const handleCellClick = useCallback(
    (params: GridCellParams, event: MouseEvent) => {
      if (!params.isEditable) {
        return;
      }

      // Ignore portal
      if (
        (event.target as any).nodeType === 1 &&
        !event.currentTarget.contains(event.target as Element)
      ) {
        return;
      }

      setCellModesModel((prevModel) => {
        return {
          // Revert the mode of the other cells from other rows
          ...Object.keys(prevModel).reduce(
            (acc, id) => ({
              ...acc,
              [id]: Object.keys(prevModel[id]).reduce(
                (acc2, field) => ({
                  ...acc2,
                  [field]: { mode: GridCellModes.View },
                }),
                {}
              ),
            }),
            {}
          ),
          [params.id]: {
            // Revert the mode of other cells in the same row
            ...Object.keys(prevModel[params.id] || {}).reduce(
              (acc, field) => ({
                ...acc,
                [field]: { mode: GridCellModes.View },
              }),
              {}
            ),
            [params.field]: { mode: GridCellModes.Edit },
          },
        };
      });
    },
    []
  );

  const handleCellModesModelChange = useCallback(
    (newModel: GridCellModesModel) => {
      setCellModesModel(newModel);
    },
    []
  );

  return (
    <>
      <Stack
        direction="row"
        justifyContent="space-between"
        alignItems="center"
        width="100%"
      >
        <Typography variant="h5">Variants</Typography>
        {newProperty === null ? (
          <Button
            startIcon={<AddCircleRounded />}
            onClick={() => setNewProperty('')}
          >
            Add Property
          </Button>
        ) : (
          <Stack direction="row" alignItems="center">
            <IconButton
              onClick={() => {
                setFieldValue(
                  `varies_by[${values.varies_by?.length}]`,
                  newProperty
                );
                values.variants.forEach((_, i) =>
                  setFieldValue(
                    `variants[${i}].varies_by[${values.varies_by?.length}]`,
                    { name: newProperty, value: '' }
                  )
                );
                setNewProperty(null);
              }}
            >
              <AddCircleRounded color="primary" />
            </IconButton>
            <TextField
              value={newProperty}
              onChange={({ target }) => setNewProperty(target.value)}
            />
          </Stack>
        )}
      </Stack>
      <Box minWidth="100%">
        <CustomDataGrid
          rows={values.variants}
          columns={columns}
          getRowId={(row) => row.ordinal || ''}
          onProcessRowUpdateError={(error) => console.log(error)}
          processRowUpdate={(newRow, oldRow) => {
            if (!newRow || !newRow.ordinal) return oldRow;
            setFieldValue(`variants[${newRow.ordinal - 1}]`, newRow);
            return newRow;
          }}
          hideFooter
        />
        <Button
          onClick={() =>
            setFieldValue(`variants[${values.variants.length}]`, {
              sku: '',
              gtin: '',
              gtin12: '',
              gtin8: '',
              varies_by: (values.varies_by || []).map((name) => ({
                name,
                value: '',
              })),
              suggested_prices: [],
              ordinal: values.variants.length + 1,
            })
          }
        >
          Add Variant
        </Button>
      </Box>
      {/* <pre>{JSON.stringify(values, null, 1)}</pre> */}
    </>
  );
};

export default CreateVariantsTable;
